import type { JobWithRelations } from "@/types/db";
import { jsonLdSafe } from "@/lib/jsonLd";
import { countryCode } from "@/lib/jobSeo";
import {
  companyDisplayName,
  isJobPostingEligible,
  jobValidThrough,
} from "@/lib/jobLifecycle";

interface JobPostingJsonLdProps {
  job: JobWithRelations;
}

function mapEmploymentType(
  tags: JobWithRelations["jobTags"],
): string | undefined {
  const jobTypeTag = tags?.find((t) => t.category?.name === "Job Type");
  if (!jobTypeTag) return undefined;

  const map: Record<string, string> = {
    "Full Time": "FULL_TIME",
    "Part Time": "PART_TIME",
    Contract: "CONTRACTOR",
    Internship: "INTERN",
  };
  return map[jobTypeTag.name];
}

const SENIORITY_MONTHS: Record<string, number> = {
  internship: 0,
  "entry level": 0,
  intermediate: 24,
  senior: 60,
  lead: 84,
  staff: 96,
  "staff+": 120,
  manager: 84,
  "manager+": 120,
};

export default function JobPostingJsonLd({ job }: JobPostingJsonLdProps) {
  if (!isJobPostingEligible(job)) return null;

  const companyName = companyDisplayName(job);
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    title: job.role,
    description: job.content,
    datePosted: job.createdAt,
    directApply: false,
    url: `https://datafirstjobs.com/${job.slug}`,
  };

  // validThrough: required-ish per Google's JobPosting guidelines. We don't
  // know the *real* expiry for a live posting, so we project 30 days from
  // datePosted — matches when the deactivate-jobs CLI hides it from the site.
  const validThrough = jobValidThrough(job);
  if (validThrough) schema.validThrough = validThrough;

  // Identifier (recommended)
  if (job.slug) {
    schema.identifier = {
      "@type": "PropertyValue",
      name: companyName,
      value: job.slug,
    };
  }

  // Hiring organization (required)
  if (companyName !== "Company") {
    const org: Record<string, string> = {
      "@type": "Organization",
      name: companyName,
    };
    if (job.company?.url) {
      org.sameAs = job.company.url;
      org.url = job.company.url;
    }
    if (job.company?.logoUrl) {
      org.logo = job.company.logoUrl;
    }
    schema.hiringOrganization = org;
  }

  // Location: remote vs physical
  if (job.isRemote) {
    schema.jobLocationType = "TELECOMMUTE";

    // Applicant location requirements for remote jobs
    if (job.country) {
      schema.applicantLocationRequirements = {
        "@type": "Country",
        name: job.country,
      };
    }
  }

  if (job.city || job.state || job.country) {
    const address: Record<string, string> = { "@type": "PostalAddress" };
    if (job.city) address.addressLocality = job.city;
    if (job.state) address.addressRegion = job.state;
    const code = countryCode(job.country);
    if (code) address.addressCountry = code;

    schema.jobLocation = {
      "@type": "Place",
      address,
    };
  }

  // Salary (recommended)
  const unitText = {
    annual: "YEAR",
    annually: "YEAR",
    year: "YEAR",
    yearly: "YEAR",
    "per year": "YEAR",
    "per annum": "YEAR",
    monthly: "MONTH",
    month: "MONTH",
    "per month": "MONTH",
    weekly: "WEEK",
    week: "WEEK",
    "per week": "WEEK",
    hourly: "HOUR",
    hour: "HOUR",
    "per hour": "HOUR",
  }[job.salaryPeriod?.toLowerCase() ?? ""];
  if (
    (job.minSalary != null || job.maxSalary != null) &&
    job.salaryCurrency &&
    unitText
  ) {
    const value: Record<string, unknown> = {
      "@type": "QuantitativeValue",
      unitText,
    };
    if (job.minSalary != null && job.maxSalary != null) {
      value.minValue = job.minSalary;
      value.maxValue = job.maxSalary;
    } else if (job.minSalary != null) {
      value.value = job.minSalary;
    } else {
      value.value = job.maxSalary;
    }

    schema.baseSalary = {
      "@type": "MonetaryAmount",
      currency: job.salaryCurrency,
      value,
    };
  }

  // Employment type (recommended)
  const employmentType = mapEmploymentType(job.jobTags);
  if (employmentType) {
    schema.employmentType = employmentType;
  }

  // Experience requirements (recommended, structured)
  if (job.seniority && job.seniority in SENIORITY_MONTHS) {
    schema.experienceRequirements = {
      "@type": "OccupationalExperienceRequirements",
      monthsOfExperience: SENIORITY_MONTHS[job.seniority],
    };
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdSafe(schema) }}
    />
  );
}
